import { Component, Input } from '@angular/core';
import { BPProfilesService } from '../../../bp-profiles.service';
import { StateService } from '@uirouter/angular';
import { STATE_BPPROFILES_OEM_OVERVIEW } from '@app/shared/app-states';
import { BaseBPProfilesDetailComponent } from '../../base-bp-profiles-detail.component';
import { Override } from '@app/shared/app-decorators';
import { FormlyType, GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { BPProfilesOEMDataService, IOEMDetail, IOEMOrderFulfillmentTime, OEMTierStatus } from '../bp-profiles-oem-data.service';
import _ from 'underscore';
import { FormattingService } from '@app/shared/services/formatting.service';
import { ILov, IValueObject } from '@type_definitions/datatypes';
import { LOV_OEM_ORDER_MODE, LOV_OEM_PROCESSING_MODE, LOV_OEM_SHIPPING_FLOW, LovService } from '@app/shared/services/lov.service';
import { util } from '@app/shared/util';
import { BPProfilesContactsDataService } from '../../contacts/bp-profiles-contacts-data.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { OFTDetailComponent } from './oft-detail.component';
import { BPProfilesTCDataService } from '../../tc/bp-profiles-tc-data.service';
import { CLFDetailComponent } from './clf-detail.component';
import { BPProfilesContactsComponent, ContactRoleTab } from '../../contacts/bp-profiles-contacts.component';
import { BPProfilesOEMUtilService, ShippingFlow } from '@app/shared/bp-profiles/bp-profiles-oem-util.service';
import { GaValidateService } from '@app/shared/formly/ga-validate.service';
import { GaStateService } from '@app/shared/services/ga-state.service';
import { SurveyDataService } from '@app/shared/survey/survey-data.service';
import { SurveyQuestionsWrapperComponent } from '@app/shared/survey/survey-questions-wrapper.component';
import { BPProfilesAttributesService, ProfileAttributeCode, IProfileAttribute, IProfileAttributeResponse } from '../../../profile-attributes/bp-profiles-attributes.service';
import { BPProfilesAccreditationsComponent } from '../../accreditations/bp-profiles-accreditations.component';
import { ACCREDITATION_TYPE_CE } from '../../accreditations/bp-profiles-accreditations.service';

export interface IOEMDetailModel extends IOEMDetail {
  shippingContactSearchInCountry?:boolean;
  invoiceContactSearchInCountry?:boolean;
  bpDefaultMarginField?:IProfileAttribute;
}

const TRANSLATION_SCREEN:string = 'BPPROFILES.OEM';

@Component({
  selector: 'bp-profiles-oem-detail',
  template: `
    <button class="btn btn-primary margin-bottom-sm" (click)="backToOverview()"><i class="fa fa-chevron-left margin-right-sm"></i> <span translate [translateParams]="{ label: ('BPPROFILES.OEM.OVERVIEW' | translate) }">GA.BUTTONS.BACK_TO_LABEL</span></button>
    
    <div class="d-block" *ngIf="loading">
      <h2><activity-indicator></activity-indicator></h2>  
    </div>
    
    <div class="d-block margin-top-sm" *ngIf="!loading">
      <h4><span class="margin-right-sm" translate [translateParams]="{ oemName: oemName }">BPPROFILES.OEM.DETAILS_TITLE</span> <i class="fa fa-star favorite"></i></h4>
      <formly [form]="form" [model]="model" [fields]="fields"></formly>
    </div>
  `
})
export class BPProfilesOEMDetailComponent extends BaseBPProfilesDetailComponent {
  @Input() public oemId:number;
  @Input() public distributorId:number;

  public oemDetail:IOEMDetail;
  public oemName:string;
  public model:IOEMDetailModel;

  private editingMode:boolean;
  private showOFT:boolean;
  private showShipping:boolean;
  private oftDetail:IOEMOrderFulfillmentTime;
  private tierStatusTitle:string;
  private resellerMarginTC:IProfileAttribute;
  private defaultProcessingModeField:IExtraFieldConfigurationObject;
  private shippingProcessField:IExtraFieldConfigurationObject;
  private shippingAddressField:IExtraFieldConfigurationObject;
  private shippingContactField:IExtraFieldConfigurationObject;
  private oftCustomField:IExtraFieldConfigurationObject;
  private orderModeField:IExtraFieldConfigurationObject;
  private oemPaymentCurrencyField:IExtraFieldConfigurationObject;
  private invoiceContactField:IExtraFieldConfigurationObject;
  private invoiceAddressField:IExtraFieldConfigurationObject;
  private bpDefaultMarginField:IExtraFieldConfigurationObject;
  private paymentTermsField:IExtraFieldConfigurationObject;
  private creditLimitField:IExtraFieldConfigurationObject;
  private contactField:IExtraFieldConfigurationObject;
  private accreditationDetailField:IExtraFieldConfigurationObject;

  constructor(
    private formlyService:GaFormlyService,
    private translationService:TranslationService,
    private formattingService:FormattingService,
    private gaStateService:GaStateService,
    private bpProfilesOEMUtilService:BPProfilesOEMUtilService,
    private bpProfilesOEMDataService:BPProfilesOEMDataService,
    private bpProfilesContactsDataService:BPProfilesContactsDataService,
    private bpProfilesAttributesService:BPProfilesAttributesService,
    private bpProfilesTCDataService:BPProfilesTCDataService,
    private lovService:LovService,
    private validateService:GaValidateService,
    protected bpProfilesService:BPProfilesService,
    protected stateService:StateService
  ) {
    super(bpProfilesService, stateService);
    // set the overview state
    this.overviewState = STATE_BPPROFILES_OEM_OVERVIEW;
  }

  @Override(BaseBPProfilesDetailComponent)
  public ngOnInit():void {
    this.editingMode = _.clone(this.bpProfilesService.editingMode);
    this.bpProfilesService.disableProfileSelection(true);
    this.gaStateService.screen = TRANSLATION_SCREEN;

    this.loading = true;
    this.bpProfilesOEMDataService.getOEMDetails(this.bpProfilesService.activeProfile?.id, this.oemId, this.distributorId)
      .then((oemDetails:IOEMDetail) => {
        this.onOEMDetailLoaded(oemDetails);
        // register listener
        this.listen();
      })
      .catch(() => {
        this.stateService.go(this.overviewState);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  @Override(BaseBPProfilesDetailComponent)
  public onEditChange(isEdit:boolean):void {
    this.editingMode = isEdit;
    super.onEditChange(isEdit);
    this.contactField.templateOptions.inputs.disableActions = isEdit;
    this.accreditationDetailField.templateOptions.inputs.disableEdit = isEdit;
    this.bpProfilesService.disableProfileSelection(true);

    if (isEdit) {
      // trigger change to update addOn label
      this.onPaymentCurrencyChange();
    }
  }

  @Override(BaseBPProfilesDetailComponent)
  public resetModel():void {
    this.form.reset({ ...this.oemDetail, shippingContactSearchInCountry: true, invoiceContactSearchInCountry: true, bpDefaultMargin: this.resellerMarginTC });
    this.form.markAsPristine();
  }

  private onOEMDetailLoaded = (detail:IOEMDetail):void => {
    this.oemDetail = util.copyAndExtend(detail);

    this.tierStatusTitle = this.isDistributor ? this.translationService.instant('BPPROFILES.OEM.DISTRIBUTOR.LABEL') : this.translationService.instant('BPPROFILES.OEM.OEM.LABEL');
    this.oemName = detail.oem?.name;
    if (this.distributorId) {
      this.oemName += ` - ${detail.companyCountry?.company?.name}`;
    }

    // initiate model and formly fields
    this.model = detail;
    // per default searching contact based on bp country should be true
    this.model.shippingContactSearchInCountry = true;
    this.model.invoiceContactSearchInCountry = true;

    // create formly fields
    this.fields = this.createFields();

    if (this.editingMode) {
      this.fillFields();
      // trigger change to update addOn label
      this.onPaymentCurrencyChange();
    }

    // update contact fields search url
    this.updateShippingSearchContactUrl();
    this.updateInvoiceSearchContactUrl();

    // trigger change for the first time
    this.onShippingProcessChange();

    // load BP default margin
    this.loadBPDefaultMargin();

    // trigger edit change for the first time
    this.onEditChange(this.bpProfilesService.editingMode);
  };

  protected fillFields():void {
    this.formlyService.fillField(
      this.defaultProcessingModeField,
      this.lovService.list(LOV_OEM_PROCESSING_MODE)
        .then((processingModes:ILov[]) => this.bpProfilesOEMUtilService.filterProcessingModes(processingModes, !!this.distributorId))
    );
    this.formlyService.fillField(this.shippingAddressField, this.bpProfilesOEMDataService.getBPLocations(this.bpProfilesService.activeProfile?.id, true));
    this.formlyService.fillField(this.invoiceAddressField, this.bpProfilesOEMDataService.getBPLocations(this.bpProfilesService.activeProfile?.id));
    this.formlyService.fillField(this.orderModeField, this.lovService.list(LOV_OEM_ORDER_MODE));
    this.formlyService.fillField(this.oemPaymentCurrencyField, this.lovService.list(`localcurrency-${this.bpProfilesService.activeProfile?.country?.id}`));
    this.fillShippingProcessField();
  }

  private fillShippingProcessField():void {
    if (!this.model.poPlacement) {
      this.formlyService.fillField(this.shippingProcessField, Promise.resolve([]));
    } else {
      this.formlyService.fillField(
        this.shippingProcessField,
        this.lovService.list(LOV_OEM_SHIPPING_FLOW)
          .then((shippingProcesses:ILov[]) => {
            // if existing value is not in the list then we need to remove it
            if (util.notNullOrUndefinedOrEmpty(this.model.shippingProcess) && !_.findWhere(shippingProcesses, { id: this.model.shippingProcess?.id })) {
              this.shippingProcessField?.formControl?.setValue(undefined);
            }
            return this.bpProfilesOEMUtilService.filterShippingFlows(shippingProcesses, this.model.poPlacement?.id);
          })
      );
    }
  }

  private loadOFTValues():void {
    this.oftCustomField.templateOptions.inputs.loading = true;
    this.bpProfilesOEMDataService.loadOFT(this.bpProfilesService.activeProfile?.id, this.model)
      .then((oftDetail:IOEMOrderFulfillmentTime) => {
        this.oftDetail = oftDetail;
        this.oftCustomField.templateOptions.inputs.oftDetail = oftDetail;
        this.showOFT = !!oftDetail?.oft;
      })
      .finally(() => {
        this.oftCustomField.templateOptions.inputs.loading = false;
      });
  }

  private loadBPDefaultMargin():void {
    this.formlyService.fillField(
      this.bpDefaultMarginField,
      this.bpProfilesTCDataService.loadTC(this.bpProfilesService.activeProfile?.id)
        .then((tcResponse:IProfileAttributeResponse) => {
          this.resellerMarginTC = _.find(tcResponse.profileAttributes, (pa:IProfileAttribute) => pa.code === ProfileAttributeCode.RESELLER_MARGIN);
          if (this.resellerMarginTC) {
            this.bpDefaultMarginField?.formControl?.setValue(this.resellerMarginTC);
          }
        })
    );
  }

  private getBPContactSearchUrl(forBPCountry?:boolean, fallbackValue?:boolean):string {
    return this.bpProfilesContactsDataService.getContactSearchUrl(this.bpProfilesService.activeProfile?.id, this.bpProfilesService.activeProfile?.company?.id, !_.isUndefined(forBPCountry) ? forBPCountry : fallbackValue);
  }

  private updateShippingSearchContactUrl = (forBPCountry?:boolean):void => {
    this.shippingContactField.templateOptions.url = this.getBPContactSearchUrl(forBPCountry, this.model.shippingContactSearchInCountry);
  };

  private updateInvoiceSearchContactUrl = (forBPCountry?:boolean):void => {
    this.invoiceContactField.templateOptions.url = this.getBPContactSearchUrl(forBPCountry, this.model.invoiceContactSearchInCountry);
  };

  private get isDistributor():boolean {
    return this.oemDetail.tierStatus?.id === OEMTierStatus.DISTRIBUTOR || !!this.distributorId;
  }

  private get isOEMCovered():boolean {
    return this.oemDetail.tierStatus?.id !== OEMTierStatus.NOT_COVERED;
  }

  protected createFields():IExtraFieldConfigurationObject[] {
    let fields:IExtraFieldConfigurationObject[] = this.createGeneralFields();

    if (this.isOEMCovered) {
      fields = fields.concat(
        this.createShippingSettingsFields(),
        this.createOFTFields(),
        this.createOrderProcessFields(),
        this.createInvoiceSettingsFields(),
        this.createAccreditationsFields(),
        this.createMarginFields(),
        this.createCLFFields(),
        this.createOEMIntelFields(),
        this.createContactsFields()
      );
    }

    return fields;
  }

  private createGeneralFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aValue('tierStatus')
        .withFormat((tierStatus:IValueObject) => this.formattingService.formatOEMType(tierStatus?.value))
        .withExtraOptions({
          expressionProperties: {
            'templateOptions.valueClass': ():string => {
              let label:string = 'tier-status-label';
              switch (this.model.tierStatus?.id) {
                case OEMTierStatus.DIRECT:
                  label += ' badge bg-success';
                break;
                case OEMTierStatus.DISTRIBUTOR:
                  label += ' badge bg-primary';
                break;
                case OEMTierStatus.NOT_COVERED:
                  label += ' badge bg-danger';
                break;
                default:
                  return label;
              }
              return label;
            }
          }
        })
        .build(),
      this.formlyService.aField(this.editingMode? FormlyType.TEXTAREA : FormlyType.VALUE, 'comments')
        .withLabelClass('col-sm-12')
        .withValueClass('col-sm-12')
        .build(),
      this.defaultProcessingModeField = this.formlyService.aField(this.editingMode? FormlyType.RADIO : FormlyType.VALUE, 'poPlacement')
        .withFormat(this.formattingService.formatLov)
        .onChange(this.onProcessingModeChange)
        .withHideExpression(() => !this.isOEMCovered)
        .build()
    ];
  }

  private createShippingSettingsFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aTitle(this.translationService.instant('BPPROFILES.OEM.SHIPPING_SETTINGS.TITLE', { label: this.tierStatusTitle }))
        .build(),
      this.shippingProcessField = this.formlyService.aField(this.editingMode? FormlyType.RADIO : FormlyType.VALUE, 'shippingProcess')
        .withHideExpression(() => !this.model.poPlacement)
        .withFormat(this.formattingService.formatLov)
        .onChange(this.onShippingProcessChange)
        .build(),
      this.shippingAddressField = this.formlyService.aField(this.editingMode? FormlyType.SELECT2 : FormlyType.VALUE, 'shipToLocation')
        .withFormat(this.formattingService.formatLocationHtml)
        .withHideExpression(() => !this.showShipping)
        .withExtraOptions({
          templateOptions: {
            parseHtml: true
          }
        })
        .build(),
      this.shippingContactField = this.formlyService.aField(this.editingMode? FormlyType.SEARCH : FormlyType.VALUE, 'shipToContact')
        .withGroupClass('no-margin-bottom')
        .withFormat(this.formattingService.formatContact)
        .withHideExpression(() => !this.showShipping)
        .withUrl(this.getBPContactSearchUrl(this.model.shippingContactSearchInCountry))
        .build(),
      this.formlyService.aCheckbox('shippingContactSearchInCountry', this.translationService.instant('BPPROFILES.CONTACTS.CONTACTSEARCHINCOUNTRY.LABEL', { country: this.bpProfilesService.activeProfile?.country?.name }))
        .withHideExpression(() => !this.showShipping || !this.editingMode)
        .withoutWrapper()
        .onChange(this.updateShippingSearchContactUrl)
        .withValueClass('offset-sm-3 col-sm-9 ps-2')
        .withWrapperClass('col-12 mt-2')
        .build()
    ];
  }

  private createOFTFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aTitle('BPPROFILES.OEM.OFT.TITLE')
        .withHideExpression(() => !this.showOFT)
        .build(),
      this.oftCustomField = this.formlyService.aCustom()
        .withoutWrapper()
        .withHideExpression(() => !this.showOFT)
        .withComponent(OFTDetailComponent)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              oftDetail: this.oftDetail,
              loading: false
            }
          }
        })
        .build()
    ];
  }

  private createOrderProcessFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aTitle('BPPROFILES.OEM.ORDER_PROCESS.TITLE')
        .build(),
      this.orderModeField = this.formlyService.aField(this.editingMode? FormlyType.RADIO : FormlyType.VALUE, 'orderMode')
        .withFormat(this.formattingService.formatLov)
        .build(),
      this.formlyService.aValue('directOrder')
        .withFormat((value:boolean):string => {
          if (util.notNullOrUndefinedOrEmpty(value)) {
            return _.findWhere(this.model.availableDirectOrderModes, { value })?.description;
          }
        })
        .withHideExpression(() => this.editingMode || !this.model.showDirectOrder && this.editingMode)
        .build(),
      this.formlyService.aRadio('directOrder')
        .withValueProp('value')
        .withLabelProp('description')
        .withOptions(this.model?.availableDirectOrderModes)
        .withHideExpression(() => !this.editingMode || !this.model.showDirectOrder)
        .build(),
      this.formlyService.aField(this.editingMode? FormlyType.SEARCH : FormlyType.VALUE, 'vendorValidator')
        .withHideExpression(() => !this.model.showVendorValidator)
        .withFormat(this.formattingService.formatContact)
        .withUrl(this.bpProfilesContactsDataService.getContactSearchUrl(this.bpProfilesService.activeProfile?.id, this.oemDetail?.oem?.id))
        .build(),
      this.formlyService.aField(this.editingMode? FormlyType.INPUT : FormlyType.VALUE, 'bpIdWithDistributor')
        .withHideExpression(() => !this.isDistributor)
        .build(),
      this.formlyService.aField(this.editingMode? FormlyType.INPUT : FormlyType.VALUE, 'distributorIdWithVendor', this.translationService.instant('BPPROFILES.OEM.DISTRIBUTORID_LABEL', { label: this.oemDetail.oem?.firstCustomLabel}))
        .withoutEditable()
        .withHideExpression(() => !this.oemDetail.oem?.firstCustomLabel)
        .build(),
      this.formlyService.aField(this.editingMode? FormlyType.INPUT : FormlyType.VALUE, 'bpIdWithVendor', this.oemDetail.oem?.firstCustomLabel)
        .withoutEditable()
        .withHideExpression(() => !this.oemDetail.oem?.firstCustomLabel)
        .build(),
      this.formlyService.aField(this.editingMode? FormlyType.INPUT : FormlyType.VALUE, 'firstPartnerIdWithVendor', this.oemDetail.oem?.secondCustomLabel)
        .withoutEditable()
        .withHideExpression(() => !this.oemDetail.oem?.secondCustomLabel)
        .build(),
      this.formlyService.aField(this.editingMode? FormlyType.INPUT : FormlyType.VALUE, 'secondPartnerIdWithVendor', this.oemDetail.oem?.thirdCustomLabel)
        .withoutEditable()
        .withHideExpression(() => !this.oemDetail.oem?.thirdCustomLabel)
        .build(),
      this.formlyService.aField(this.editingMode? FormlyType.TEXTAREA : FormlyType.VALUE, 'orderModeComments')
        .build()
    ];
  }

  private createInvoiceSettingsFields():IExtraFieldConfigurationObject[] {
    const clipLevelInfo:string = `<a href="/ga2/#/faq/CLIP_LEVEL_EXCHANGE_RATE" target="_blank">${this.translationService.instant('BPPROFILES.OEM.FAQ.CLIP_LEVEL')}</a>`;

    return [
      this.formlyService.aTitle(this.translationService.instant('BPPROFILES.OEM.INVOICE_SETTINGS.TITLE', { label: this.tierStatusTitle }))
        .build(),
      this.oemPaymentCurrencyField = this.formlyService.aField(this.editingMode? FormlyType.SELECT : FormlyType.VALUE, 'paymentCurrency')
        .showInfo()
        .withFormat(this.formattingService.formatCurrencyS)
        .withoutInfoOffset()
        .onChange(this.onPaymentCurrencyChange)
        .build(),
      this.formlyService.aField(this.editingMode? FormlyType.CHECKBOX : FormlyType.VALUE, 'confidential')
        .showInfo()
        .withFormat(this.formattingService.formatBoolean)
        .withoutInfoOffset()
        .build(),
      this.paymentTermsField = this.formlyService.aField(this.editingMode? FormlyType.INPUT : FormlyType.VALUE, 'paymentTerms')
        .withFormat(this.formattingService.formatDays)
        .withInputType(FormlyType.NUMBER)
        .withValueClass('col-sm-4')
        .withInfo(`<a href="/ga2/#/faq/PAYMENT_TERMS_OEM" target="_blank">${this.translationService.instant('BPPROFILES.OEM.FAQ.PAYMENT_TERMS')}</a>`)
        .showInfo()
        .withExtraOptions({
          templateOptions: {
            min: 0,
            showAddOn: this.editingMode
          }
        })
        .build(),
      this.creditLimitField = this.formlyService.aField(this.editingMode? FormlyType.INPUT : FormlyType.VALUE, 'creditLimit')
        .withInputType(FormlyType.NUMBER)
        .withValueClass('col-sm-4')
        .withInfo(`<a href="/ga2/#/faq/CREDIT_LIMIT_OEM" target="_blank">${this.translationService.instant('BPPROFILES.OEM.FAQ.CREDIT_LIMIT')}</a>`)
        .withFormat((value:number) => this.formattingService.formatAmount(value, this.model.paymentCurrency))
        .showInfo()
        .withExtraOptions({
          templateOptions: {
            min: 0,
            showAddOn: this.editingMode
          }
        })
        .build(),
      this.formlyService.aValue('agreesToClipLevel')
        .withFormat((value:string) => this.model.agreesToClipLevel ? `${_.findWhere(this.bpProfilesOEMDataService.getClipLevelOptions(), { id: value })?.value}: ${this.formattingService.formatPct(this.model.percentageClipLevel)}` : '-')
        .withHideExpression(() => this.editingMode)
        .withInfo(clipLevelInfo)
        .showInfo()
        .build(),
      this.formlyService.aRadio('agreesToClipLevel')
        .withHideExpression(() => !this.editingMode)
        .withInfo(clipLevelInfo)
        .showInfo()
        .withOptions(this.bpProfilesOEMDataService.getClipLevelOptions())
        .withValueProp('id')
        .withLabelProp('value')
        .build(),
      this.formlyService.anInput('percentageClipLevel')
        .withInputType(FormlyType.NUMBER)
        .withValueClass('col-sm-2')
        .isRequired()
        .withHideExpression(() => !this.editingMode || !this.model.agreesToClipLevel)
        .withAddOn('%')
        .withExtraOptions({
          templateOptions: {
            min: 0
          }
        })
        .build(),
      this.invoiceAddressField = this.formlyService.aField(this.editingMode? FormlyType.SELECT2 : FormlyType.VALUE, 'invoiceToLocation')
        .withFormat(this.formattingService.formatLocationHtml)
        .withExtraOptions({
          templateOptions: {
            parseHtml: true
          }
        })
        .build(),
      this.invoiceContactField = this.formlyService.aField(this.editingMode? FormlyType.SEARCH : FormlyType.VALUE, 'invoiceToContact')
        .withGroupClass('no-margin-bottom')
        .withFormat(this.formattingService.formatContact)
        .withUrl(this.getBPContactSearchUrl(this.model.invoiceContactSearchInCountry))
        .build(),
      this.formlyService.aCheckbox('invoiceContactSearchInCountry', this.translationService.instant('BPPROFILES.CONTACTS.CONTACTSEARCHINCOUNTRY.LABEL', { country: this.bpProfilesService.activeProfile?.country?.name }))
        .withHideExpression(() => !this.editingMode)
        .withoutWrapper()
        .onChange(this.updateInvoiceSearchContactUrl)
        .withValueClass('offset-sm-3 col-sm-9 ps-2')
        .withWrapperClass('col-12 mt-2')
        .build()
    ];
  }

  private createAccreditationsFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aTitle('BPPROFILES.OEM.ACCREDITATIONS.TITLE')
        .build(),
      this.accreditationDetailField = this.formlyService.aCustom()
        .withoutWrapper()
        .withComponent(BPProfilesAccreditationsComponent)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              profileId: this.bpProfilesService.activeProfile?.id,
              vendorId: this.oemDetail?.oem?.id,
              accreditationType: ACCREDITATION_TYPE_CE,
              disableActions: true,
              gridAutoHeight: true
            }
          }
        })
        .build()
    ];
  }

  private createMarginFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aTitle('BPPROFILES.OEM.MARGIN.TITLE')
        .build(),
      this.bpDefaultMarginField = this.formlyService.aValue('bpDefaultMargin')
        .withFormat((value:IProfileAttribute) => this.formattingService.formatPct(this.bpProfilesAttributesService.getRealAttributeValue(value)))
        .build(),
      this.formlyService.aValue('webshopVendorMargin')
        .withFormat((value:number) => value ? this.formattingService.formatPct(value, true) : '-')
        .build()
    ];
  }

  private createCLFFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aTitle('CLF')
        .build(),
      this.formlyService.aCustom()
        .withoutWrapper()
        .withComponent(CLFDetailComponent)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              countryId: this.bpProfilesService.activeProfile?.country?.id,
              oemCompanyId: this.oemDetail.oem?.id
            }
          }
        })
        .build()
    ];
  }

  private createOEMIntelFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aTitle('BPPROFILES.OEM.INTEL.TITLE')
        .build(),
      this.formlyService.aCustom()
        .withoutWrapper()
        .withComponent(SurveyQuestionsWrapperComponent)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              countryId: this.bpProfilesService.activeProfile?.country?.id,
              companyId: this.oemDetail.oem?.id,
              workflowType: SurveyDataService.WORKFLOW_OEM_COUNTRY,
              isEmbedded: true
            }
          }
        })
        .build()
    ];
  }

  private createContactsFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aTitle('BPPROFILES.CONTACTS.TEXT')
        .build(),
      this.contactField = this.formlyService.aCustom()
        .withoutWrapper()
        .withComponent(BPProfilesContactsComponent)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              selectedRoleTab: ContactRoleTab.MISSING_ROLES,
              hideRoleSelector: true,
              gridAutoHeight: true,
              companyId: this.isDistributor ? this.oemDetail?.companyCountry?.company?.id : this.oemDetail?.oem?.id,
              vendorCompanyId: this.isDistributor ? this.oemDetail?.oem?.id : undefined,
              disableActions: this.editingMode,
              hideOptionsColumn: true
            }
          }
        })
        .build()
    ];
  }

  private onProcessingModeChange = ():void => {
    this.fillShippingProcessField();
    this.loadOFTValues();
  };

  private onShippingProcessChange = ():void => {
    this.loadOFTValues();

    this.showShipping = _.contains([
      ShippingFlow.VENDOR_PARTNER_CUSTOMER,
      ShippingFlow.VENDOR_DISTRIBUTOR_PARTNER_CUSTOMER,
      ShippingFlow.DISTRIBUTOR_PARTNER_CUSTOMER
    ], this.model.shippingProcess?.id);
  };

  private onPaymentCurrencyChange = ():void => {
    this.creditLimitField.templateOptions.addOn = this.model.paymentCurrency?.id;
    this.paymentTermsField.templateOptions.addOn = this.translationService.instant('GA.GENERAL.DAYS');
  };

  private prepareRequestData():IOEMDetail {
    // remove stuff that is no longer applicable
    // if shipping location and contact are invisible, we clear them
    if (!this.showShipping) {
      this.model.shipToLocation = undefined;
      this.model.shipToContact = undefined;
    }
    const data = util.copyAndExtend(this.oemDetail, this.model);
    // clean unused data model for server
    delete data.shippingContactSearchInCountry;
    delete data.invoiceContactSearchInCountry;
    delete data.bpDefaultMarginField;

    return data;
  }

  protected onSave = ():void => {
    this.validateService.validateFields(this.fields, this.model, true)
      .then(() => {
        let promise:Promise<IOEMDetail>;
        const profileId:number = this.bpProfilesService.activeProfile?.id;

        this.bpProfilesService.onSaveBusy.emit(true);

        if (this.isDistributor) {
          promise = <Promise<IOEMDetail>>this.bpProfilesOEMDataService.updateDistributor(profileId, Number(this.oemId), this.prepareRequestData());
        } else {
          promise = <Promise<IOEMDetail>>this.bpProfilesOEMDataService.updateOEMRelation(profileId, this.prepareRequestData());
        }

        promise
          .then(this.onOEMDetailLoaded)
          .finally(() => {
            this.bpProfilesService.onSaveBusy.emit(false);
          });
      })
      .catch(util.noop);
  };

  @Override(BaseBPProfilesDetailComponent)
  public ngOnDestroy() {
    super.ngOnDestroy();
    this.bpProfilesService.disableProfileSelection(false);
    this.gaStateService.screen = undefined;
  }
}